import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {type CustomMetricKey, sendCustomMetric} from '@github-ui/stats'
import type {ProfilerOnRenderCallback, ReactNode} from 'react'
import {useCallback, useMemo} from 'react'

export const IPM_TELEMETRY_FEATURE_FLAG = 'in_product_messaging_datadog_monitoring'

const IDLE_CALLBACK_TIMEOUT = 2000

const IPM_METRIC_KEYS = {
  'in_product_messaging.component.render': 'IPM_COMPONENT_RENDER',
  'in_product_messaging.component.error': 'IPM_COMPONENT_ERROR',
  'in_product_messaging.component.visible': 'IPM_COMPONENT_VISIBLE',
  'in_product_messaging.component.dismiss': 'IPM_COMPONENT_DISMISS',
  'in_product_messaging.action': 'IPM_ACTION',
} as const satisfies Record<string, CustomMetricKey>

export type IpmMetricName = keyof typeof IPM_METRIC_KEYS
export type IpmRenderTimingPhase = 'mount' | 'update'
export type IpmAction = 'dismiss' | 'cta_click' | 'submit' | 'impression' | 'click' | 'error'
type IpmTagKey = 'package' | 'component' | 'display_location' | 'surface' | 'category' | 'nudge_id' | 'variant'

export type IpmMetricTags = Partial<Record<IpmTagKey, string>> & {
  action?: IpmAction
  phase?: IpmRenderTimingPhase
}

interface SendIpmMetricParams {
  name: IpmMetricName
  value: number
  tags?: IpmMetricTags
  requestUrl?: string
  samplingProbability?: number
  /**
   * Defer enqueueing to an idle callback. Safe for the render-timing metric
   * (fired from a Profiler, never tied to navigation), but unsafe for discrete
   * action/dismiss/error metrics fired on clicks that trigger navigation: the
   * page can unload before the idle callback runs, so the metric never even
   * reaches the `@github-ui/stats` batch and its `pagehide` flush has nothing to
   * send. The stats batch already idle-schedules the actual network flush, so a
   * synchronous enqueue does not block render/INP. Defaults to `true`.
   */
  defer?: boolean
}

interface IpmCountMetricOptions {
  requestUrl?: string
  samplingProbability?: number
  value?: number
}

export interface IpmRenderTiming {
  onRender: ProfilerOnRenderCallback
  isEnabled: boolean
}

export interface IpmErrorBoundaryProps {
  children: ReactNode
  tags: IpmMetricTags
  fallback?: ReactNode
  boundaryName?: string
}

const noopRenderTiming: IpmRenderTiming = {
  onRender: () => {},
  isEnabled: false,
}

function canSendIpmMetrics() {
  return typeof window !== 'undefined' && isFeatureEnabled(IPM_TELEMETRY_FEATURE_FLAG)
}

function requestUrl() {
  return typeof window === 'undefined' ? undefined : window.location.href
}

function defer(callback: () => void) {
  if (typeof window === 'undefined') return

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, {timeout: IDLE_CALLBACK_TIMEOUT})
  } else {
    window.setTimeout(callback, 0)
  }
}

function renderPhase(phase: string): IpmRenderTimingPhase {
  return phase === 'mount' ? 'mount' : 'update'
}

function definedTags(tags?: IpmMetricTags) {
  if (!tags) return undefined

  return Object.fromEntries(Object.entries(tags).filter((entry): entry is [string, string] => entry[1] !== undefined))
}

export function sendIpmMetric({
  name,
  value,
  tags,
  requestUrl: metricRequestUrl,
  samplingProbability,
  defer: shouldDefer = true,
}: SendIpmMetricParams) {
  if (!canSendIpmMetrics()) return

  const emit = () => {
    sendCustomMetric(
      {
        name: IPM_METRIC_KEYS[name],
        value,
        tags: definedTags(tags),
        requestUrl: metricRequestUrl ?? requestUrl(),
      },
      false,
      samplingProbability,
    )
  }

  if (shouldDefer) {
    defer(emit)
  } else {
    emit()
  }
}

export function useIpmRenderTiming(tags: IpmMetricTags, samplingProbability?: number): IpmRenderTiming {
  const {isBrowser} = useRuntimeEnvironment()
  const isEnabled = isBrowser && isFeatureEnabled(IPM_TELEMETRY_FEATURE_FLAG)
  const {
    package: packageName,
    component,
    display_location: displayLocation,
    surface,
    category,
    nudge_id: nudgeId,
    variant,
  } = tags

  return useMemo(() => {
    if (!isEnabled) {
      return noopRenderTiming
    }

    const onRender: ProfilerOnRenderCallback = (_id, phase, actualDuration) => {
      sendIpmMetric({
        name: 'in_product_messaging.component.render',
        value: actualDuration,
        tags: {
          package: packageName,
          component,
          display_location: displayLocation,
          surface,
          category,
          nudge_id: nudgeId,
          variant,
          phase: renderPhase(phase),
        },
        samplingProbability,
      })
    }

    return {onRender, isEnabled: true}
  }, [category, component, displayLocation, isEnabled, nudgeId, packageName, samplingProbability, surface, variant])
}

export function IpmErrorBoundary({
  children,
  tags,
  fallback = null,
  boundaryName = 'ipm-error-boundary',
}: IpmErrorBoundaryProps) {
  const {isBrowser} = useRuntimeEnvironment()
  const isEnabled = isBrowser && isFeatureEnabled(IPM_TELEMETRY_FEATURE_FLAG)
  const onError = useCallback(() => {
    sendIpmMetric({
      name: 'in_product_messaging.component.error',
      value: 1,
      tags,
      samplingProbability: 1,
      defer: false,
    })
  }, [tags])

  if (!isEnabled) {
    return children
  }

  return (
    <ErrorBoundary boundaryName={boundaryName} critical fallback={fallback} onError={onError}>
      {children}
    </ErrorBoundary>
  )
}

export function reportIpmVisible(tags: IpmMetricTags, options: IpmCountMetricOptions = {}) {
  sendIpmMetric({
    name: 'in_product_messaging.component.visible',
    value: options.value ?? 1,
    tags,
    requestUrl: options.requestUrl,
    samplingProbability: options.samplingProbability,
    defer: false,
  })
}

export function reportIpmDismiss(tags: IpmMetricTags, options: IpmCountMetricOptions = {}) {
  sendIpmMetric({
    name: 'in_product_messaging.component.dismiss',
    value: options.value ?? 1,
    tags: {...tags, action: 'dismiss'},
    requestUrl: options.requestUrl,
    samplingProbability: options.samplingProbability,
    defer: false,
  })
}

export function reportIpmAction(action: IpmAction, tags: IpmMetricTags, options: IpmCountMetricOptions = {}) {
  sendIpmMetric({
    name: 'in_product_messaging.action',
    value: options.value ?? 1,
    tags: {...tags, action},
    requestUrl: options.requestUrl,
    samplingProbability: options.samplingProbability,
    defer: false,
  })
}
